import { useMutation, useQuery, useQueryClient, type Query } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  fetchPipelinePreview,
  fetchPipelineStatus,
  triggerPipeline,
  type PipelinePreviewResponse,
  type PipelineStartStage,
  type PipelineStatusResponse,
  type TriggerPipelinePayload,
} from '../services/experiment-pipeline.service';

type RefetchIntervalFn = (
  query: Query<PipelineStatusResponse, Error, PipelineStatusResponse>,
) => number | false | undefined;

interface UsePipelineStatusOptions {
  /** Poll the status endpoint while the pipeline is in flight. Function form lets callers branch on the current data. */
  refetchInterval?: number | false | RefetchIntervalFn;
  enabled?: boolean;
}

/**
 * Fetches the live pipeline-status row. Caller controls polling cadence —
 * the detail page passes a function that returns 5s while in-flight then
 * stops once a terminal status arrives so we don't burn requests.
 */
export function usePipelineStatus(experimentId: string, options: UsePipelineStatusOptions = {}) {
  const { refetchInterval = false, enabled = true } = options;
  return useQuery<PipelineStatusResponse>({
    queryKey: queryKeys.experiments.pipelineStatus(experimentId),
    queryFn: () => fetchPipelineStatus(experimentId),
    refetchInterval,
    enabled,
  });
}

interface UsePipelinePreviewOptions {
  /** Drop to false when the consuming surface is hidden so we don't burn a request on a tab that's not visible. */
  enabled?: boolean;
}

/**
 * Read-only pre-trigger forecast: candidate count + PROCEED/WARN/BLOCK
 * recommendation. Wired into the "Generate for Full Catalog" surface so we can
 * disable the button when there's nothing to enrich, or surface a soft
 * warning when the candidate pool is small enough that the A/B split + outlier
 * filter typically eats it all.
 *
 * The candidate count only changes when content scoring runs — there's no
 * polling here. Re-fetched on demand by invalidating
 * `queryKeys.experiments.pipelinePreview(id)` from the content-scoring trigger
 * mutation when that path lands.
 */
export function usePipelinePreview(experimentId: string, options: UsePipelinePreviewOptions = {}) {
  const { enabled = true } = options;
  return useQuery<PipelinePreviewResponse>({
    queryKey: queryKeys.experiments.pipelinePreview(experimentId),
    queryFn: () => fetchPipelinePreview(experimentId),
    enabled,
    // Backend is cheap (one count query) but the result is stable until the
    // user re-runs scoring — a generous stale window keeps tab-switches fast.
    staleTime: 30_000,
  });
}

interface TriggerArgs {
  experimentId: string;
  startFromStage?: PipelineStartStage;
}

/**
 * Mutation that (re-)triggers the experiment pipeline. Invalidates the
 * pipeline-status + experiment-detail caches on success so the in-progress
 * card and the retry buttons disable immediately. 409s and pre-condition
 * failures surface as toasts with the server's reason string.
 * Reads `localStorage('subBatchSize')` at fire time and merges it into the
 * payload; absent or invalid values fall back to the server default.
 */
export function useTriggerPipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ experimentId, startFromStage }: TriggerArgs) => {
      const raw = localStorage.getItem('subBatchSize');
      const parsed = raw !== null ? parseInt(raw, 10) : NaN;
      const subBatchSize = !isNaN(parsed) && parsed > 0 ? parsed : undefined;
      const payload: TriggerPipelinePayload = {
        ...(startFromStage ? { startFromStage } : {}),
        ...(subBatchSize !== undefined ? { subBatchSize } : {}),
      };
      return triggerPipeline(experimentId, payload);
    },
    onSuccess: (_data, { experimentId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.experiments.pipelineStatus(experimentId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.experiments.detail(experimentId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.experiments.all });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Could not trigger the pipeline. Please try again.'));
    },
  });
}
